import { Mesh, RingGeometry, TorusGeometry, PlaneGeometry, Vector3 } from 'three';
import { Ability } from './Ability.js';
import { settings } from '../config/settings.js';
import { ParticleShape } from '../particles/ParticleSystem.js';
import { DecalType } from '../effects/GroundDecals.js';
import { frame } from '../core/FrameUniforms.js';
import { LAYER } from '../core/Layers.js';
import { saturate, Easing, lerp } from '../utils/math.js';
import { getColor } from '../utils/color.js';
import {
  ConstructStyle,
  ConstructGlyph,
  createConstructMaterial,
  createConstructGlyphMaterial,
  syncConstructMaterial
} from '../materials/ConstructMaterial.js';

const MAX_BLADES = 9;
const _pos = new Vector3();
const _dir = new Vector3();
const _emit = {};

/**
 * RAZOR CHOIR — a line converted into a timed comb of transverse cuts.
 *
 * The travelling note plants dormant crescents. Only when it reaches the end
 * do they perform, near-to-far, each crossing the lane at right angles to the
 * cast. The interesting dodge is longitudinal and rhythmic; widening the line
 * is not the answer.
 */
export class RazorChoirAbility extends Ability {
  constructor(context) {
    super('razor', context);
  }

  createShaders() {
    this.bladeGeometry = new RingGeometry(0.46, 1, 36, 1, -Math.PI * 0.47, Math.PI * 0.94);
    this.scarGeometry = new PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.blades = [];
    this.scars = [];

    for (let i = 0; i < MAX_BLADES; i++) {
      const bladeMaterial = createConstructMaterial(ConstructStyle.BLADE);
      const blade = new Mesh(this.bladeGeometry, bladeMaterial);
      blade.layers.set(LAYER.VFX);
      blade.renderOrder = 13;
      blade.visible = false;
      this.group.add(blade);
      this.blades.push({ mesh: blade, material: bladeMaterial, deposited: false, fired: false });

      const scarMaterial = createConstructGlyphMaterial(ConstructGlyph.SLASH);
      const scar = new Mesh(this.scarGeometry, scarMaterial);
      scar.layers.set(LAYER.VFX);
      scar.renderOrder = 8;
      scar.visible = false;
      this.group.add(scar);
      this.scars.push({ mesh: scar, material: scarMaterial });
    }

    this.noteGeometry = new TorusGeometry(1, 0.08, 8, 32);
    this.noteMaterial = createConstructMaterial(ConstructStyle.ENERGY);
    this.note = new Mesh(this.noteGeometry, this.noteMaterial);
    this.note.layers.set(LAYER.VFX);
    this.note.renderOrder = 13;
    this.group.add(this.note);
    this._seed = 0;
    this._finale = false;
  }

  createParticles() {
    this.streaks = this.ctx.particles.get('razor.streaks', {
      capacity: 3600,
      shape: ParticleShape.STREAK,
      additive: true,
      stretch: true,
      softFade: 0.25
    });
    this.streaks.uniforms.uGravity.value.set(0, -1.2, 0);
    this.streaks.uniforms.uDrag.value = 0.8;
    this.streaks.uniforms.uEndSize.value = 0.08;
    this.streaks.uniforms.uFadeOut.value = 0.32;
  }

  get impactDuration() {
    const c = settings.razor;
    return Math.max(0.2, (c.bladeCount - 1) * c.stagger + c.slashTime + c.holdTime);
  }

  get fadeDuration() {
    return Math.max(0.1, settings.razor.fadeTime);
  }

  get instanceCount() {
    return Math.min(MAX_BLADES, Math.round(settings.razor.bladeCount)) * 2 + 1;
  }

  onSpawn() {
    this._seed = Math.random() * 10;
    this._finale = false;
    this.note.visible = true;
    for (const record of this.blades) {
      record.deposited = false;
      record.fired = false;
      record.mesh.visible = false;
    }
    for (const scar of this.scars) scar.mesh.visible = false;
  }

  _count() {
    return Math.max(3, Math.min(MAX_BLADES, Math.round(settings.razor.bladeCount)));
  }

  _bladePoint(index, count, out) {
    const s = (index + 0.65) / (count + 0.3);
    return this.pointAt(s, out);
  }

  _syncNote() {
    const c = settings.razor;
    this.note.position.copy(this.position).setY(c.travelHeight);
    this.note.rotation.set(Math.PI / 2, Math.atan2(this.direction.x, this.direction.z), this.age * c.noteSpin);
    this.note.scale.setScalar(c.noteSize);
    syncConstructMaterial(this.noteMaterial, c, {
      seed: this._seed,
      reveal: 1,
      fade: 1,
      pulse: 0.5 + Math.sin(this.age * 16) * 0.5
    });
  }

  _emitSlash(position, count, direction, speed) {
    const c = settings.razor;
    const g = settings.global;
    _emit.position = position;
    _emit.radius = c.particleRadius;
    _emit.direction = direction;
    _emit.speed = speed * g.particleSpeed;
    _emit.speedVariance = 0.5;
    _emit.spread = 0.28;
    _emit.inherit = null;
    _emit.anchor = null;
    _emit.size = c.particleSize * g.particleSize;
    _emit.sizeVariance = 0.6;
    _emit.life = c.particleLife * g.particleLifetime;
    _emit.lifeVariance = 0.35;
    _emit.spin = 0;
    _emit.tint = null;
    _emit.time = frame.uTime.value;
    this.streaks.emit(Math.round(count * g.particleCount), _emit);
  }

  onTravel() {
    const c = settings.razor;
    const count = this._count();
    this._syncNote();

    for (let i = 0; i < MAX_BLADES; i++) {
      const record = this.blades[i];
      const active = i < count;
      if (!active) {
        record.mesh.visible = false;
        continue;
      }
      const threshold = (i + 0.65) / (count + 0.3);
      if (this.u >= threshold && !record.deposited) {
        record.deposited = true;
        this._bladePoint(i, count, _pos);
        this._emitSlash(_pos, c.plantSparks, _dir.set(0, 1, 0), c.plantSpeed);
      }
      if (!record.deposited) continue;

      this._bladePoint(i, count, record.mesh.position);
      record.mesh.position.addScaledVector(this.side, -c.halfWidth).setY(c.bladeHeight * 0.55);
      record.mesh.rotation.set(0, Math.atan2(this.direction.x, this.direction.z), -0.45);
      record.mesh.scale.set(c.bladeSize, c.bladeSize * 0.72, c.bladeSize);
      record.mesh.visible = true;
      syncConstructMaterial(record.material, c, {
        seed: this._seed + i,
        reveal: 1,
        fade: 1,
        pulse: 0.2
      });
    }
  }

  onImpact() {
    const c = settings.razor;
    const count = this._count();
    this.note.visible = false;
    this.position.copy(this.pointAt(0.5, _pos));
    for (let i = 0; i < count; i++) this.blades[i].deposited = true;
    this.lightBoost = c.arrivalLight;
  }

  onFade(_dt, t) {
    const c = settings.razor;
    const count = this._count();
    const fade = t <= 1 ? 1 : 1 - (t - 1);
    const yaw = Math.atan2(this.direction.x, this.direction.z);

    for (let i = 0; i < MAX_BLADES; i++) {
      const record = this.blades[i];
      const scar = this.scars[i];
      if (i >= count) {
        record.mesh.visible = false;
        scar.mesh.visible = false;
        continue;
      }

      const local = saturate((this.impactTime - i * c.stagger) / Math.max(0.01, c.slashTime));
      const sweep = Easing.inOutCubic(local);
      this._bladePoint(i, count, _pos);
      record.mesh.position.copy(_pos).addScaledVector(this.side, lerp(-c.halfWidth, c.halfWidth, sweep));
      record.mesh.position.y = c.bladeHeight + Math.sin(local * Math.PI) * c.slashLift;
      record.mesh.rotation.set(0, yaw, lerp(-0.65, 0.75, sweep) + this.impactTime * c.bladeSpin);
      const pop = 0.55 + Math.sin(Math.min(1, local) * Math.PI) * 0.45;
      record.mesh.scale.set(c.bladeSize * pop, c.bladeSize * 0.72 * pop, c.bladeSize * pop);
      record.mesh.visible = local > 0.001 && fade > 0.001;
      syncConstructMaterial(record.material, c, {
        seed: this._seed + i,
        reveal: Easing.outCubic(saturate(local * 2)),
        fade: fade * (1 - saturate((local - 0.82) / 0.18) * 0.45),
        pulse: Math.sin(local * Math.PI)
      });

      scar.mesh.position.copy(_pos).setY(c.scarHeight);
      scar.mesh.rotation.y = yaw;
      scar.mesh.scale.set(c.halfWidth * 2.35, c.scarLength, 1);
      scar.mesh.visible = local > 0.12 && fade > 0.001;
      syncConstructMaterial(scar.material, c, {
        seed: this._seed + i * 1.7,
        reveal: Easing.outCubic(saturate((local - 0.1) * 2.5)),
        fade: fade * (1 - saturate((local - 0.6) / 0.4) * 0.6),
        pulse: 1 - local
      });

      if (local > 0.08 && !record.fired) {
        record.fired = true;
        const sideSign = i % 2 ? -1 : 1;
        this._emitSlash(record.mesh.position, c.slashSparks, _dir.copy(this.side).multiplyScalar(sideSign), c.slashSpeed);
        this.ctx.decals.spawn(DecalType.SHOCKWAVE, _pos, {
          radius: c.scarBurstRadius,
          life: c.scarLife,
          intensity: c.scarIntensity,
          width: 0.035,
          colorA: getColor(c.colorA),
          colorB: getColor(c.colorC)
        });
        this.ctx.shake.add(c.beatShake * settings.global.cameraShake, 3.2);
      }
    }

    const finaleAt = (count - 1) * c.stagger + c.slashTime * 0.45;
    if (!this._finale && this.impactTime >= finaleAt) {
      this._finale = true;
      this.lightBoost = c.impactLight;
      this.ctx.flash.trigger(getColor(c.colorC), c.impactFlash);
    }
  }

  onDestroy() {
    this.note.visible = false;
    for (const record of this.blades) record.mesh.visible = false;
    for (const scar of this.scars) scar.mesh.visible = false;
  }

  dispose() {
    this.bladeGeometry.dispose();
    this.scarGeometry.dispose();
    this.noteGeometry.dispose();
    this.noteMaterial.dispose();
    for (const record of this.blades) record.material.dispose();
    for (const scar of this.scars) scar.material.dispose();
    super.dispose();
  }
}
